import type { SupportedLocale as Lang } from "@site-types/content"
import { useRouterState } from "@tanstack/react-router"
import { defaultLocale, isSupportedLocale } from "@utils/locale"
import type { ReactNode } from "react"
import { createContext, useContext } from "react"

type RoutePageName = string

export type RoutePageContextValue = {
  locale: Lang
  currentPage: RoutePageName
  pageClass: string
  pathname: string
}

type RoutePageContextProviderProps = {
  children: ReactNode
  locale?: Lang
  page?: string
  pathname?: string
}

const DEFAULT_PATH = "/"

export const RoutePageContext = createContext<RoutePageContextValue | undefined>(undefined)

function browserPathnameFallback() {
  return typeof window === "undefined" ? DEFAULT_PATH : window.location.pathname || DEFAULT_PATH
}

function shouldUseBrowserPathnameFallback() {
  return typeof process !== "undefined" && process.env.NODE_ENV === "test"
}

export function routeLocaleFromPathname(pathname: string): Lang {
  const firstSegment = pathname.split("?")[0]?.split("#")[0]?.split("/").filter(Boolean)[0]
  return isSupportedLocale(firstSegment) ? firstSegment : defaultLocale
}

export function routePageFromPathname(pathname: string): string {
  const segments = pathname.split("?")[0]?.split("#")[0]?.split("/").filter(Boolean)
  const page = segments[0] === "en" || segments[0] === "ja" ? segments[1] : segments[0]
  return page || "index"
}

export function routePageClass(page: string) {
  return page.replace(/[^a-z0-9_-]/gi, "-")
}

export function resolveRoutePageState({
  locale,
  page,
  pathname = DEFAULT_PATH,
}: {
  locale?: Lang
  page?: string
  pathname?: string
}): RoutePageContextValue {
  const safePathname = pathname || DEFAULT_PATH
  const derivedLocale = routeLocaleFromPathname(safePathname)
  const derivedPage = routePageFromPathname(safePathname)
  const finalLocale = locale ?? derivedLocale
  const finalPage = page ?? derivedPage
  return {
    locale: finalLocale,
    currentPage: finalPage,
    pageClass: routePageClass(finalPage),
    pathname: safePathname,
  }
}

export function useRoutePagePathname() {
  if (shouldUseBrowserPathnameFallback()) return browserPathnameFallback()

  try {
    return useRouterState({ select: (state) => state.location.pathname })
  } catch {
    return browserPathnameFallback()
  }
}

export function RoutePageContextProvider({
  children,
  locale,
  page,
  pathname,
}: RoutePageContextProviderProps) {
  const sourcePath = pathname ?? useRoutePagePathname()
  const value = resolveRoutePageState({ locale, page, pathname: sourcePath })

  return <RoutePageContext.Provider value={value}>{children}</RoutePageContext.Provider>
}

export function useRoutePageContext() {
  const context = useContext(RoutePageContext)

  if (context) return context

  const pathname = useRoutePagePathname()

  const routePageContextMessage =
    "useRoutePageContext must be used within a RoutePageContextProvider. " +
    "Wrap the page with PageLayout to provide locale/page state."

  if (typeof process !== "undefined" && process.env.NODE_ENV !== "test") {
    if (typeof console !== "undefined" && import.meta.env?.DEV) {
      console.warn(routePageContextMessage)
    }

    throw new Error(routePageContextMessage)
  }

  return resolveRoutePageState({ pathname })
}
